import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import "./PebblesAdmin.css";

const base = `${process.env.REACT_APP_BACKEND_URL}/admin/pebbles/carefund`;
const money = kobo => new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format((Number(kobo) || 0) / 100);
const date = value => value ? new Date(value).toLocaleString("en-GB", { timeZone: "Africa/Lagos" }) : "-";
const display = value => String(value || "-").replaceAll("_", " ");
const api = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });

export default function CareFundAdmin() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [canceling, setCanceling] = useState(null);
  const [notice, setNotice] = useState("");
  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const response = await axios.get(base, { ...api(), params: { page } });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load CareFund records.");
    } finally {
      setBusy(false);
    }
  }, [page]);
  useEffect(() => { load(); }, [load]);
  async function retryCancellation(row) {
    if (!window.confirm(`Retry Paystack cancellation for ${row.phone}? This attempts to stop recurring deductions; it does not withdraw or erase saved contributions.`)) return;
    setCanceling(row._id); setError(""); setNotice("");
    try {
      const response = await axios.post(`${base}/${row._id}/cancel`, { confirm: true }, api());
      setNotice(response.data.canceled ? "Paystack cancellation confirmed." : (response.data.message || "Cancellation still pending. Check Paystack before assuming deductions stopped."));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Cancellation could not be verified. Do not assume deductions stopped.");
    } finally { setCanceling(null); }
  }
  const metrics = data?.metrics || {};
  const fields = [
    ["Recurring contributions", money(metrics.totalSavedKobo)],
    ["Pebbles airtime/data contributions", money(metrics.pebblesFeesKobo)],
    ["Combined recorded contributions", money(metrics.combinedContributionsKobo)],
    ["Contributors", metrics.totalContributors || 0],
    ["Active contributors", metrics.activeContributors || 0],
    ["Pending mandates", metrics.pendingContributors || 0],
    ["Contributors with failed deductions", metrics.failedContributors || 0],
    ["Successful contributions", metrics.successfulContributions || 0],
    ["Paystack fees reported", money(metrics.totalFeesKobo)],
  ];
  return <section aria-label="CareFund by Pebbles administration" className="pebbles-carefund">
    <div className="pebbles-carefund-heading">
      <div><h2>CareFund by Pebbles</h2><p>Recurring health contributions and recorded savings, separate from airtime and data revenue.</p></div>
      <button type="button" onClick={load} disabled={busy} className="pebbles-carefund-refresh"><RefreshCw size={16} /> Refresh</button>
    </div>
    {error && <p role="alert" className="pebbles-error">{error}</p>}
    {notice && <p role="status" className="pebbles-notice">{notice}</p>}
    {busy && !data && <p role="status">Loading CareFund information...</p>}
    {data && <>
      <div className="pebbles-metrics">{fields.map(([title, value]) => <div key={title}><span>{title}</span><strong>{value}</strong></div>)}</div>
      <p className="pebbles-notice">Totals include recorded recurring contributions plus Pebbles transaction fees from completed, verified live airtime/data orders. They are historical contribution records, not segregated insurance funds, current coverage, or proof of a withdrawal-ready balance. Failed deduction counts identify contributors with any recorded failure, including historical failures.</p>
      <div className="pebbles-table-wrap"><table><thead><tr><th>Contributor</th><th>Plan</th><th>Status</th><th>Recurring</th><th>Pebbles fees</th><th>Total recorded</th><th>Payments</th><th>Last payment (WAT)</th><th>Action</th></tr></thead><tbody>
        {(data.contributors || []).map(row => <tr key={row._id}>
          <td>{row.phone || "-"}<small>{row.email || "-"}</small></td>
          <td>{row.planKey || "-"}<small>{money(row.amountKobo)} · {row.interval || "-"}</small></td>
          <td><span className="pebbles-status">{display(row.state)}</span>{row.lastFailureAt && <small>Failure recorded: {date(row.lastFailureAt)}</small>}</td>
          <td>{money(row.savedKobo)}</td>
          <td>{money(row.pebblesFeesKobo)}<small>{row.pebblesFeeOrders || 0} orders</small></td>
          <td><strong>{money(row.combinedContributionsKobo)}</strong></td>
          <td>{row.contributionCount || 0}</td>
          <td>{date(row.lastPaidAt)}</td>
          <td>{row.state === "cancellation_pending" ? <button type="button" className="pebbles-carefund-refresh" disabled={Boolean(canceling)} onClick={() => retryCancellation(row)}>{canceling === row._id ? "Checking..." : "Retry cancellation"}</button> : "-"}</td>
        </tr>)}
        {!data.contributors?.length && <tr><td colSpan={9} className="pebbles-empty">No CareFund enrollments found.</td></tr>}
      </tbody></table></div>
      <div className="pebbles-pagination">
        <span>{data.count || 0} contributors · Page {page + 1} of {Math.max(1, Math.ceil((data.count || 0) / (data.pageSize || 20)))}</span>
        <button type="button" aria-label="Previous CareFund page" disabled={busy || page === 0} onClick={() => setPage(x => x - 1)}><ChevronLeft size={18} /></button>
        <button type="button" aria-label="Next CareFund page" disabled={busy || (page + 1) * (data.pageSize || 20) >= (data.count || 0)} onClick={() => setPage(x => x + 1)}><ChevronRight size={18} /></button>
      </div>
    </>}
  </section>;
}
